/**
 * Admin → Tools.
 *
 * One row per registered tool. The "Confirmation" cell is the live override
 * — admins set "always confirm", "never confirm", or "use default" per tool.
 * PATCH /tools/{name} with `requires_confirmation: true | false | null`.
 */
import { useMemo, useState } from "react";

import { adminApi, type AdminTool } from "../../api/admin";
import { PageHeader } from "../../components/PageHeader";
import { Pill } from "../../components/Pill";
import { QueryBoundary } from "../../components/QueryBoundary";
import { useToast } from "../../components/toast/context";

type Override = "default" | "always" | "never";

function overrideOf(tool: AdminTool): Override {
  if (tool.requires_confirmation_override === true) return "always";
  if (tool.requires_confirmation_override === false) return "never";
  return "default";
}

function payloadOf(value: Override): boolean | null {
  if (value === "always") return true;
  if (value === "never") return false;
  return null;
}

export function ToolsPage() {
  const toolsQuery = adminApi.tools.list();
  const patchMutation = adminApi.tools.patch();
  const toast = useToast();

  const [filter, setFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const categories = useMemo(() => {
    const s = new Set<string>();
    for (const t of toolsQuery.data ?? []) s.add(t.category);
    return Array.from(s).sort();
  }, [toolsQuery.data]);

  const filtered = useMemo(() => {
    const list = toolsQuery.data ?? [];
    return list.filter((t) => {
      if (categoryFilter && t.category !== categoryFilter) return false;
      if (
        filter &&
        !t.name.toLowerCase().includes(filter.toLowerCase()) &&
        !t.category.toLowerCase().includes(filter.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [toolsQuery.data, filter, categoryFilter]);

  const handleOverrideChange = (tool: AdminTool, value: Override) => {
    patchMutation.mutate(
      { name: tool.name, payload: { requires_confirmation: payloadOf(value) } },
      {
        onSuccess: () => toast.ok(`${tool.name} updated`),
        onError: (e) => toast.error(e),
      },
    );
  };

  return (
    <>
      <PageHeader
        title="Tools"
        description="Registered Jarvis tools. Set per-tool confirmation overrides — they take effect on the next call."
      />

      <div className="flex items-end gap-4 mb-5 flex-wrap">
        <div className="flex-1 max-w-md">
          <label className="label" htmlFor="tools-search">
            Search
          </label>
          <input
            id="tools-search"
            className="input"
            placeholder="filter by name or category…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="tools-category">
            Category
          </label>
          <select
            id="tools-category"
            className="input"
            style={{ minWidth: 180 }}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="text-sm text-ink-low pb-2">
          <span className="tnum font-medium text-ink">{filtered.length}</span> /{" "}
          {toolsQuery.data?.length ?? 0} listed
        </div>
      </div>

      <QueryBoundary
        query={toolsQuery}
        empty={
          <div className="empty-state">
            <div className="title">No tools registered</div>
            <p className="hint">Tools are picked up from the @typed_tool registry.</p>
          </div>
        }
        isEmpty={() => filtered.length === 0}
      >
        {() => (
          <div className="panel panel--flush overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Tool</th>
                  <th>Category</th>
                  <th>Workspaces</th>
                  <th>MCP</th>
                  <th>Default</th>
                  <th>Confirmation</th>
                  <th>Audit-safe args</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((tool) => {
                  const override = overrideOf(tool);
                  return (
                    <tr key={tool.name}>
                      <td>
                        <span className="font-mono text-sm text-ink">
                          {tool.name}
                        </span>
                      </td>
                      <td className="text-sm text-ink-medium">
                        {tool.category}
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {tool.workspaces.map((w) => (
                            <span key={w} className="tag-chip">
                              {w}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        {tool.mcp_exposed ? (
                          <Pill value="active" />
                        ) : (
                          <Pill value="paused" />
                        )}
                      </td>
                      <td>
                        {tool.mcp_dangerous_default ? (
                          <span className="text-yellow-dark text-xs font-medium">
                            confirm
                          </span>
                        ) : (
                          <span className="text-ink-low text-xs">
                            silent
                          </span>
                        )}
                      </td>
                      <td>
                        <select
                          className="input"
                          style={{ minWidth: 160, padding: "6px 10px" }}
                          value={override}
                          onChange={(e) =>
                            handleOverrideChange(
                              tool,
                              e.target.value as Override,
                            )
                          }
                          disabled={patchMutation.isPending}
                        >
                          <option value="default">Default</option>
                          <option value="always">Always confirm</option>
                          <option value="never">Never confirm</option>
                        </select>
                      </td>
                      <td className="text-xs text-ink-low">
                        {tool.audit_safe_args.length === 0
                          ? "—"
                          : tool.audit_safe_args.join(", ")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </QueryBoundary>
    </>
  );
}
